import { useEffect, useState } from "react";
import { fetchConeras, fetchStats, Conera, Stats } from "../services/api";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export default function Dashboard() {
  const [coneras, setConeras] = useState<Conera[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState("todas");
  const [statusFilter, setStatusFilter] = useState("todas");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([fetchConeras(), fetchStats()])
      .then(([c, s]) => { setConeras(c); setStats(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function getGroup(id: string) {
    const parts = id.split("-");
    return parts.length > 1 ? parts[1] : "OTRO";
  }

  const groups = ["todas", ...Array.from(new Set(coneras.map((c) => getGroup(c.id))))];

  function isUpdated(c: Conera) {
    return c.downloaded_version >= c.latest_version;
  }

  function getStatus(c: Conera) {
    const now = Date.now();
    const h26 = 26 * 60 * 60 * 1000;
    const d3 = 3 * 24 * 60 * 60 * 1000;

    if (!c.last_check_at) return { label: "Sin datos", color: "bg-zinc-800 text-zinc-500", filter: "sindatos" };
    if (!isUpdated(c)) return { label: "Pendiente", color: "bg-orange-500/15 text-orange-400", filter: "no" };
    if ((now - c.last_check_at) > d3) return { label: "Sin conexion", color: "bg-red-500/15 text-red-400", filter: "sinconexion" };
    if ((now - c.last_check_at) > h26) return { label: "Atrasada", color: "bg-amber-500/15 text-amber-400", filter: "desactualizada" };
    return { label: "OK", color: "bg-emerald-500/15 text-emerald-400", filter: "ok" };
  }

  const filtered = coneras.filter((c) => {
    if (search && !c.id.toLowerCase().includes(search.toLowerCase()) && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (groupFilter !== "todas" && getGroup(c.id) !== groupFilter) return false;
    const status = getStatus(c);
    if (statusFilter !== "todas" && status.filter !== statusFilter) return false;
    return true;
  });

  const total = coneras.length;
  const statusCounts = coneras.reduce((acc, c) => { const s = getStatus(c).filter; acc[s] = (acc[s] || 0) + 1; return acc; }, {} as Record<string, number>);

  function formatDate(ts: number | null) {
    if (!ts) return "-";
    return new Date(ts).toLocaleDateString("es-MX", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  const statCards = [
    { label: "Total", value: total, color: "text-zinc-100", bg: "bg-zinc-800" },
    { label: "OK", value: statusCounts["ok"] || 0, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Atrasada", value: statusCounts["desactualizada"] || 0, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Pendiente", value: statusCounts["no"] || 0, color: "text-orange-400", bg: "bg-orange-500/10" },
    { label: "Sin conexion", value: statusCounts["sinconexion"] || 0, color: "text-red-400", bg: "bg-red-500/10" },
    { label: "Sin datos", value: statusCounts["sindatos"] || 0, color: "text-zinc-500", bg: "bg-zinc-800" },
  ];

  const chartData = stats?.downloadsByDay.map((d) => ({
    day: new Date(d.day * 86400000).toLocaleDateString("es-MX", { day: "numeric", month: "short" }),
    descargas: d.count,
  })) || [];

  const pieData = [
    { name: "OK", value: statusCounts["ok"] || 0, color: "#34d399" },
    { name: "Atrasada", value: statusCounts["desactualizada"] || 0, color: "#fbbf24" },
    { name: "Pendiente", value: statusCounts["no"] || 0, color: "#f97316" },
    { name: "Sin conexion", value: statusCounts["sinconexion"] || 0, color: "#f87171" },
    { name: "Sin datos", value: statusCounts["sindatos"] || 0, color: "#71717a" },
  ].filter((d) => d.value > 0);

  function exportCSV() {
    const headers = ["ID", "Nombre", "Estado", "Version Descargada", "Version Disponible", "Ultima Consulta", "Ultima Descarga"];
    const rows = filtered.map((c) => [
      c.id, c.name, getStatus(c).label,
      c.downloaded_version > 0 ? `V${c.downloaded_version}` : "",
      c.latest_version > 0 ? `V${c.latest_version}` : "",
      c.last_check_at ? new Date(c.last_check_at).toISOString() : "",
      c.last_downloaded_at ? new Date(c.last_downloaded_at).toISOString() : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coneras-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const topConeras = stats?.coneraActivity.slice(0, 5) || [];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-zinc-100">Dashboard</h1>
        <button onClick={exportCSV} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium rounded-lg transition-colors">
          Exportar CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3 mb-6">
        {statCards.map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 border border-zinc-800`}>
            <p className="text-xs text-zinc-500 uppercase tracking-wide">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color} mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zinc-100 mb-3">Descargas (30 dias)</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: "8px", color: "#fff", fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa" }}
                />
                <Bar dataKey="descargas" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-zinc-600 text-sm text-center py-8">Sin datos aun</p>
          )}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zinc-100 mb-3">Estado general</h2>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: "8px", color: "#fff", fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-zinc-600 text-sm text-center py-8">Sin datos</p>
          )}
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            {pieData.map((d) => (
              <div key={d.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                <span className="text-xs text-zinc-400">{d.name} ({d.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {topConeras.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
          <h2 className="text-sm font-semibold text-zinc-100 mb-3">Top 5 coneras activas</h2>
          <div className="space-y-2">
            {topConeras.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300 font-medium">{c.id}</span>
                <span className="text-zinc-500">{c.total_checks} consultas</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 items-start sm:items-center">
        <div className="relative w-full sm:w-auto">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar conera..."
            className="w-full sm:w-48 bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-3 py-1.5 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500 uppercase">Grupo</span>
          <div className="flex gap-1 overflow-x-auto">
            {groups.map((g) => (
              <button
                key={g}
                onClick={() => setGroupFilter(g)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  groupFilter === g ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {g === "todas" ? "Todas" : g}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500 uppercase">Estado</span>
          <div className="flex gap-1">
            {[
              { v: "todas", l: "Todas" },
              { v: "ok", l: "OK" },
              { v: "desactualizada", l: "Atrasada" },
              { v: "no", l: "Pendiente" },
              { v: "sinconexion", l: "Sin conexion" },
              { v: "sindatos", l: "Sin datos" },
            ].map((s) => (
              <button
                key={s.v}
                onClick={() => setStatusFilter(s.v)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  statusFilter === s.v ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-8 text-center">Cargando coneras...</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase">ID</th>
                <th className="text-left px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase hidden sm:table-cell">Nombre</th>
                <th className="text-center px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Estado</th>
                <th className="text-center px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase hidden md:table-cell">Descargada</th>
                <th className="text-center px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase hidden md:table-cell">Disponible</th>
                <th className="text-right px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase hidden lg:table-cell">Consulta</th>
                <th className="text-right px-3 md:px-4 py-3 text-xs font-medium text-zinc-500 uppercase hidden lg:table-cell">Descarga</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-zinc-800/50 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                  onClick={() => navigate("/conera/" + c.id)}
                >
                  <td className="px-3 md:px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zinc-100">{c.id}</span>
                      {c.client_version < 2 && c.last_check_at && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/15 text-red-400" title="Updater desactualizado">v{c.client_version}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 md:px-4 py-3 text-zinc-400 hidden sm:table-cell">{c.name}</td>
                  <td className="px-3 md:px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatus(c).color}`}>
                      {getStatus(c).label}
                    </span>
                  </td>
                  <td className="px-3 md:px-4 py-3 text-center text-zinc-400 hidden md:table-cell">
                    {c.downloaded_version > 0 ? `V${c.downloaded_version}` : "-"}
                  </td>
                  <td className="px-3 md:px-4 py-3 text-center text-zinc-400 hidden md:table-cell">
                    {c.latest_version > 0 ? `V${c.latest_version}` : "-"}
                  </td>
                  <td className="px-3 md:px-4 py-3 text-right text-zinc-500 text-xs hidden lg:table-cell">{formatDate(c.last_check_at)}</td>
                  <td className="px-3 md:px-4 py-3 text-right text-zinc-500 text-xs hidden lg:table-cell">{formatDate(c.last_downloaded_at)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-600">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-zinc-600 mt-3">Mostrando {filtered.length} de {total} coneras</p>
    </div>
  );
}
